import {
  IonButton,
  IonIcon,
  IonModal,
  IonText,
  IonSpinner,
  IonToast,
} from "@ionic/react";
import { useRef, useEffect } from "react";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";
import {
  downloadOutline,
  closeOutline,
  shareSocialOutline,
} from "ionicons/icons";
import { useRecoilState } from "recoil";
import { toastState } from "recoil/ToastAtom";
import CloseSvg from "assets/md-close.svg";
import CheckIcon from "assets/check_icon.svg";
import { useGetExamCertificate } from "hooks/examen/useGetExamCertificate";
import "./CertificadoExamenModal.css";

interface IModal {
  isOpen: boolean;
  handleClose: () => void;
  sessionName: string;
  userName: string;
  result: number;
  idBasis?: string | number;
  idModule?: string | number;
  examDate?: number; // Unix timestamp from certificates service
  onGoToProgress?: () => void;
  hideProgressPrompt?: boolean;
}

export const CertificadoExamenModal: React.FC<IModal> = ({
  isOpen,
  handleClose,
  sessionName,
  result,
  idBasis,
  idModule,
  onGoToProgress,
  hideProgressPrompt = false,
}) => {
  const modal = useRef<HTMLIonModalElement>(null);
  const [toast, setToast] = useRecoilState(toastState);
  const { base64, loading, error, handleGetExamCertificate, resetCertificate } =
    useGetExamCertificate();
  useEffect(() => {
    if (isOpen && idBasis != null && idModule != null) {
      handleGetExamCertificate(idBasis, idModule);
    } else {
      resetCertificate();
    }
    return () => resetCertificate();
  }, [isOpen, idBasis, idModule, handleGetExamCertificate, resetCertificate]);

  function dismiss() {
    resetCertificate();
    handleClose();
    modal.current?.dismiss();
  }

  const showSuccessToast = () => {
    setToast({
      isOpen: true,
      color: "success",
      message: "Compartido correctamente",
      icon: CheckIcon,
      button: [
        {
          icon: CloseSvg,
          side: "end",
          role: "cancel",
          cssClass: "toast-button",
        },
      ],
    });
  };

  const showErrorToast = () => {
    setToast({
      isOpen: true,
      color: "danger",
      message: "No se pudo compartir",
      icon: CloseSvg,
      button: [
        {
          icon: CloseSvg,
          side: "end",
          role: "cancel",
          cssClass: "toast-button",
        },
      ],
    });
  };

  const handleDownload = async () => {
    if (!base64) return;
    const isImage = base64.startsWith("/9j/") || base64.startsWith("iVBOR");
    const ext = isImage ? "jpg" : "pdf";
    const fileName = `certificado-${sessionName.replace(/\s+/g, "-")}.${ext}`;
    const mimeType = isImage ? "image/jpeg" : "application/pdf";

    try {
      const platform = Capacitor.getPlatform();
      let successMessage = "El certificado se guardó correctamente";

      if (platform === "web") {
        // Web: trigger blob download to Downloads folder
        const blob = await (
          await fetch(`data:${mimeType};base64,${base64}`)
        ).blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        successMessage = "El certificado se descargó correctamente";
      } else {
        // Native: try Documents first - plugin auto-requests permission on Android
        const writeOptions = {
          path: fileName,
          data: base64,
          directory: Directory.Documents,
          recursive: true,
        };
        try {
          await Filesystem.requestPermissions();
          await Filesystem.writeFile(writeOptions);
          successMessage = "El certificado se guardó en Documentos";
        } catch (permErr) {
          const isPermissionDenied =
            (permErr as Error)?.message?.includes("permission") ||
            (permErr as Error)?.message?.includes("denied");
          if (platform === "android" && isPermissionDenied) {
            await Filesystem.writeFile({
              ...writeOptions,
              directory: Directory.Cache,
            });
            successMessage = "El certificado se guardó correctamente";
          } else {
            throw permErr;
          }
        }
      }

      setToast({
        isOpen: true,
        color: "success",
        message: successMessage,
        icon: CheckIcon,
        button: [
          {
            icon: CloseSvg,
            side: "end",
            role: "cancel",
            cssClass: "toast-button",
          },
        ],
      });
    } catch {
      setToast({
        isOpen: true,
        color: "danger",
        message: "No se pudo descargar el certificado",
        icon: CloseSvg,
        button: [
          {
            icon: CloseSvg,
            side: "end",
            role: "cancel",
            cssClass: "toast-button",
          },
        ],
      });
    }
  };

  const handleShare = async () => {
    if (!base64) return;
    const shareText = `He conseguido un resultado de ${result}% en el examen de ${sessionName}`;
    const isImage = base64.startsWith("/9j/") || base64.startsWith("iVBOR");
    const ext = isImage ? "jpg" : "pdf";
    const fileName = `certificado-${sessionName.replace(/\s+/g, "-")}.${ext}`;
    try {
      const platform = Capacitor.getPlatform();
      const { uri } = await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Cache,
      });

      if (platform === "ios" || platform === "android") {
        // title = email subject; text = message body (used by Gmail, etc.)
        // Note: Native share sheet may not preview text for all apps
        await Share.share({
          title: `Certificado - ${sessionName}`,
          text: shareText,
          dialogTitle: "Compartir certificado",
          files: [uri],
        });
        showSuccessToast();
      } else if (navigator.share) {
        const isImage = base64.startsWith("/9j/") || base64.startsWith("iVBOR");
        const mimeType = isImage ? "image/jpeg" : "application/pdf";
        const ext = isImage ? "jpg" : "pdf";
        const blob = await (
          await fetch(`data:${mimeType};base64,${base64}`)
        ).blob();
        const file = new File([blob], fileName, { type: mimeType });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            text: shareText,
            files: [file],
          });
          showSuccessToast();
        } else {
          await navigator.clipboard.writeText(shareText);
          setToast({
            isOpen: true,
            color: "success",
            message: "Texto copiado al portapapeles",
            icon: CheckIcon,
            button: [
              {
                icon: CloseSvg,
                side: "end",
                role: "cancel",
                cssClass: "toast-button",
              },
            ],
          });
        }
      } else {
        await navigator.clipboard.writeText(shareText);
        setToast({
          isOpen: true,
          color: "success",
          message: "Texto copiado al portapapeles",
          icon: CheckIcon,
          button: [
            {
              icon: CloseSvg,
              side: "end",
              role: "cancel",
              cssClass: "toast-button",
            },
          ],
        });
      }
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        showErrorToast();
      }
    }
  };

  const showCertificateError = () => {
    const message =
      idBasis == null || idModule == null
        ? "No se puede cargar el certificado para este módulo"
        : "No se pudo cargar el certificado";
    setToast({
      isOpen: true,
      color: "danger",
      message,
      icon: CloseSvg,
      button: [
        {
          icon: CloseSvg,
          side: "end",
          role: "cancel",
          cssClass: "toast-button",
        },
      ],
    });
  };

  const canShareDownload = !!base64 && !loading;
  const isDisabled = loading || !base64;

  return (
    <IonModal
      isOpen={isOpen}
      ref={modal}
      className="ul-modal certificado-examen-modal"
      onDidDismiss={dismiss}
    >
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "15px 20px 16px",
            borderBottom: "1px solid var(--ion-color-light)",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <IonText style={{ fontSize: "18px", fontWeight: 600 }}>
            Certificado
          </IonText>
          <IonButton color="medium" fill="clear" onClick={dismiss}>
            <IonIcon slot="icon-only" icon={closeOutline} />
          </IonButton>
        </div>
        <div
          style={{
            padding: "0 20px 20px",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
          }}
        >
          {loading && <IonSpinner name="crescent" />}
          {!loading && base64 && (() => {
            const isImage = base64.startsWith("/9j/") || base64.startsWith("iVBOR");
            if (isImage) {
              return (
                <img
                  src={`data:image/jpeg;base64,${base64}`}
                  alt="Certificado"
                  style={{ display: "block", width: "100%" }}
                />
              );
            }
            return (
              <embed
                src={`data:application/pdf;base64,${base64}`}
                type="application/pdf"
                style={{ width: "100%", height: "400px" }}
              />
            );
          })()}
          {!loading && !base64 && (
            <IonText color="medium" style={{ textAlign: "center", fontSize: 14 }}>
              No se pudo cargar el certificado
            </IonText>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            padding: "0 20px 20px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <IonButton
              color="orange"
              expand="block"
              disabled={isDisabled}
              onClick={canShareDownload ? handleShare : showCertificateError}
              style={{ flex: 1, minWidth: "120px", height: "44px", margin: 0 }}
            >
              {loading ? (
                <IonSpinner name="crescent" />
              ) : (
                <>
                  <IonIcon slot="start" icon={shareSocialOutline} />
                  Compartir
                </>
              )}
            </IonButton>
            <IonButton
              color="orange"
              fill="outline"
              expand="block"
              disabled={isDisabled}
              onClick={canShareDownload ? handleDownload : showCertificateError}
              style={{ flex: 1, minWidth: "120px", height: "44px", margin: 0 }}
            >
              <IonIcon slot="start" icon={downloadOutline} />
              Descargar
            </IonButton>
          </div>
          {!hideProgressPrompt && (
            <>
              <div
                style={{
                  width: "100%",
                  height: "1px",
                  background: "var(--ion-color-light)",
                  marginTop: "5px",
                  marginBottom: "5px",
                }}
              />
              <div style={{ width: "100%" }}>
                <p
                  style={{
                    fontFamily: "Lato, sans-serif",
                    fontSize: "14px",
                    color: "black",
                    margin: "0 0 12px 0",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  Puedes ver tus certificaciones dentro de progreso
                </p>
                <IonButton
                  color="orange"
                  fill="clear"
                  expand="block"
                  onClick={() => {
                    dismiss();
                    onGoToProgress?.();
                  }}
                  style={{ margin: 0, height: "40px" }}
                >
                  Ir a progreso
                </IonButton>
              </div>
            </>
          )}
        </div>
      </div>
      <IonToast
        isOpen={toast.isOpen}
        message={toast.message}
        color={toast.color}
        icon={toast.icon}
        cssClass="css-success-toast"
        buttons={toast.button}
        onDidDismiss={() => setToast((prev) => ({ ...prev, isOpen: false }))}
        duration={2000}
      />
    </IonModal>
  );
};
